// @ts-check
// Campanha de e-mail a partir de uma lista de cards (spec/publico.md seção 14.4; contrato 09 de público e de e-mail):
// "Campanha de e-mail" da lista copia os filtros, os grupos e a busca do rascunho da view (sem a view ao vivo); a
// seleção, ou a lista aberta por um clique num gráfico em modo SNAPSHOT, vira a lista de ids. Só para o card do funil.
import { CAMPAIGN_AUDIENCE_OBJECTS } from '../../../../shared/email/email-settings.js';
import { audienceFromRecordIds, audienceFromViewState, AUDIENCE_LIMITS } from '../../../../shared/audiences/audience-definition.js';
import { navigate } from '../../app/router.js';
import { createEmailCampaign } from '../../data/email.js';
import { findManyRecords } from '../../data/rest.js';
import { drilldownModeOf } from '../../lib/drilldown-links.js';
import { emailCampaignHref } from '../../lib/email-campaigns.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { getViewDraft, getViewQuery } from '../views/view-state.js';

/** @param {string} objectNameSingular */
export const isCampaignAudienceObject = (objectNameSingular) => CAMPAIGN_AUDIENCE_OBJECTS.includes(/** @type {any} */ (objectNameSingular));

/**
 * Público da lista como está na tela (rascunho da view, com filtros ainda não salvos).
 * @param {import('../../data/metadata-store.js').View} view
 */
export function audienceFromListView(view) {
  const draft = getViewDraft(view);
  return audienceFromViewState({ filters: draft.filters.value, filterGroups: draft.filterGroups.value, anyFieldSearch: draft.anyFieldSearch.value }, { usage: 'AUDIENCE' });
}

/**
 * Rascunho de campanha com o público da lista aberta ou dos cards selecionados; abre o assistente no passo Público.
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, view: import('../../data/metadata-store.js').View,
 *   ids?: string[] | null }} input
 */
export async function startCampaignFromList({ objectMetadata, view, ids = null }) {
  const query = getViewQuery(view);
  let recordIds = ids;
  if (!recordIds && drilldownModeOf(query.drilldown) === 'SNAPSHOT') recordIds = await listScopedIds(objectMetadata, query);
  const audienceDefinition = recordIds ? audienceFromRecordIds(recordIds) : audienceFromListView(view).definition;
  const campaign = await createEmailCampaign({
    name: recordIds
      ? t('emailCampaigns.fromSelectionName', { count: recordIds.length })
      : t('emailCampaigns.fromViewName', { view: translateMetadataLabel(view.name) }),
    audienceDefinition,
  });
  showSuccessSnackbar(t('emailCampaigns.draftCreated'));
  navigate(emailCampaignHref(campaign.id));
}

/**
 * Ids da lista aberta por um clique num gráfico (snapshot), página a página, até o teto de ids do público.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata @param {ReturnType<typeof getViewQuery>} query
 */
async function listScopedIds(objectMetadata, query) {
  /** @type {string[]} */
  const ids = [];
  let cursor = /** @type {string | null | undefined} */ (undefined);
  do {
    const page = await findManyRecords(objectMetadata, { filter: query.filter, orderBy: query.orderBy, limit: 200, depth: 0, startingAfter: cursor, drilldown: query.drilldown });
    ids.push(...page.records.map((record) => record.id));
    cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
  } while (cursor && ids.length < AUDIENCE_LIMITS.recordIds);
  return ids.slice(0, AUDIENCE_LIMITS.recordIds);
}
